import { useLazyQuery } from "@apollo/client";
import {
  Card,
  PartialPage,
  Stack,
  Text,
  View,
  Button,
} from "@towersystems/roam-common-native";
import { Sale } from "@towersystems/roam-common/lib/generated-types";
import { QUERY_SALE, useTransferSale } from "../../../sales/utils";
import React from "react";
import { SaleLineItemsSummary } from "../../../sales/components/SaleLineItemsSummary";
import { SaleCustomerSummary } from "../../../sales/components/SaleCustomerSummary";
import { SaleTotalsSummary } from "../../../sales/components/SaleTotalsSummary";
import { Spacings } from "react-native-ui-lib";
import { useSwitchSale } from "../../utils/sale-controller";
import { ActionsRootParamList } from "../../../../types";
import { StackScreenProps } from "@react-navigation/stack";

export type PartialConfirmTransferWebSaleScreenProps = StackScreenProps<
  ActionsRootParamList,
  "PartialConfirmTransferWebSaleScreen"
>;

export const PartialConfirmTransferWebSaleScreen = ({
  navigation,
  route,
}: PartialConfirmTransferWebSaleScreenProps) => {
  const [transferring, setTransferring] = React.useState(false);
  const [transferError, setTransferError] = React.useState<string | undefined>(
    undefined
  );
  const { transferWebSale } = useTransferSale();
  const [q, { loading }] = useLazyQuery<{ sale: Sale }>(QUERY_SALE);
  const [error, setError] = React.useState<string | undefined>(undefined);
  const switchSale = useSwitchSale();

  const { saleId } = route?.params;

  const [sale, setSale] = React.useState<Sale | undefined>(undefined);

  React.useEffect(() => {
    q({
      variables: {
        id: saleId,
      },
    })
      .then((result) => {
        if (result.data?.sale) {
          const sale = result.data.sale;
          if (sale?.source !== "WEB_CHECKOUT" || sale?.state !== "SAVED") {
            throw new Error("Sale is not a web checkout sale");
          }

          setSale(sale);
        }
      })
      .catch((err) => {
        setError("Invalid Sale");
      });
  }, [saleId]);

  const handleOnTransferSale = React.useCallback((sale: Sale) => {
    setTransferring(true);
    setTransferError(undefined);
    transferWebSale(sale.id)
      .then((result) => {
        return switchSale(result).then(() => {
          navigation.pop();
        });
      })
      .catch((err) => {
        setTransferError("Something went wrong.");
        setTransferring(false);
        setError(err.message);
      });
  }, []);

  const handleOnClose = () => {
    navigation.goBack();
  };

  const errMarkup = error ? (
    <Stack vertical>
      <View center>
        <Text center variant="heading">
          Opps!
        </Text>
        <Text center>{error}</Text>
      </View>
      <View center>
        <Button label="Scan another QR code" onPress={handleOnClose}></Button>
      </View>
    </Stack>
  ) : null;

  const loadingMarkup = loading ? <Text>Loading...</Text> : null;

  const bodyMarkup =
    sale && !loading && !error ? (
      <View>
        <Stack vertical>
          <View>
            <Text variant="heading">Sale Found!</Text>
            <Text>
              Sale will be accessible in roam once the transfer is complete.
            </Text>
          </View>

          {sale.lineItems.length >= 1 ? (
            <Card title="Items" filled>
              <View>
                <SaleLineItemsSummary sale={sale} />
              </View>
            </Card>
          ) : undefined}
          <Card title="Totals" filled>
            <SaleTotalsSummary sale={sale} />
          </Card>
          {sale.customer ? (
            <Card title="Customer" filled>
              <SaleCustomerSummary customer={sale.customer} />
            </Card>
          ) : undefined}
        </Stack>
      </View>
    ) : undefined;

  const footerMarkup = sale ? (
    <View style={{ padding: Spacings.s4 }}>
      <Stack>
        <View flex>
          <Button fullWidth label="Cancel" onPress={handleOnClose} />
        </View>
        <View flex>
          <Button
            disabled={transferring}
            isAction
            fullWidth
            label={
              transferring
                ? "Transferring..."
                : transferError
                ? "Try Again"
                : "Transfer Sale"
            }
            onPress={() => handleOnTransferSale(sale)}
          />
        </View>
      </Stack>
    </View>
  ) : undefined;

  return (
    <PartialPage
      scrollable={true}
      size="compact"
      onClose={handleOnClose}
      wrapperStyles={{
        backgroundColor: "rgba(0,0,0,0.3)",
      }}
      footer={footerMarkup}
      contentContainerStyle={{
        flex: 1,
        minHeight: "auto",
      }}
    >
      {errMarkup}
      {loadingMarkup}
      {bodyMarkup}
    </PartialPage>
  );
};
